import { useState } from "react";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Textarea } from "@component/textarea";
import { AlertCircleIcon, CheckCircleIcon } from "lucide-react";
import { updateInterviewStatus } from "@service/interview";
import { BugReportRequest } from "@service/bugs";
import { interviewStatusPendingInterview } from "@constant/app";
import { trackEvent } from "@util/mixpanel";

interface RetakeRequestDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (reason: string) => void;
    userId: string; // Add userId prop
}

export default function RetakeRequestDialog({
    isOpen,
    onClose,
    onSubmit,
    userId,
}: RetakeRequestDialogProps) {
    const [reason, setReason] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = () => {
        if (!reason.trim()) return;

        setIsSubmitting(true);




        updateInterviewStatus({ "new_status": interviewStatusPendingInterview, "retake_reason": reason })
            .then((res) => {
                console.log("Updated status from retake", res);
                trackEvent("interview_retake", {
                    "reason": reason
                })
            })
            .then(() => {
                onSubmit(reason);
                setIsSubmitting(false);
                setIsSubmitted(true);
                setReason("");
            })
    };

    const handleClose = () => {
        setIsSubmitted(false);
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                {!isSubmitted ? (
                    <>
                        <DialogHeader className="flex flex-col items-center text-center space-y-4 pt-4">
                            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                                <AlertCircleIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <DialogTitle className="text-xl font-semibold">
                                Request to Retake Interview
                            </DialogTitle>
                            <p className="text-muted-foreground px-4">
                                Please explain why you need to retake this interview. Our team
                                will review your request and respond within 24 hours.
                            </p>
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            <Textarea
                                placeholder="Please provide a reason for retaking the interview..."
                                value={reason}
                                onChange={(e: any) => setReason(e.target.value)}
                                className="min-h-[120px]"
                            />
                        </div>

                        <DialogFooter className="gap-3 sm:gap-0">
                            <Button
                                variant="outline"
                                onClick={handleClose}
                                className="w-full sm:w-auto"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                                onClick={handleSubmit}
                                disabled={!reason.trim() || isSubmitting}
                            >
                                {isSubmitting ? "Submitting..." : "Submit Request"}
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <DialogHeader className="flex flex-col items-center text-center space-y-4 pt-4">
                            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <DialogTitle className="text-xl font-semibold">
                                Request Submitted
                            </DialogTitle>
                            <p className="text-muted-foreground px-4">
                                Thank you for submitting your request to retake the interview.
                                Our team will review your request and respond within 24 hours
                                via email.
                            </p>
                        </DialogHeader>

                        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg my-2">
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                Request ID:{" "}
                                <span className="font-mono">
                                    {Math.random().toString(36).substring(2, 10).toUpperCase()}
                                </span>
                            </p>
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                                onClick={handleClose}
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
